import React, { useEffect, useState } from "react";
import { useSimpleEmpresa } from "@/hooks/useSimpleEmpresa";
// import { useEmpresaApi } from "@/api/multiEmpresaAdapter"; // Migrado a useSimpleEmpresa
import * as clientesApiService from "@/services/clientesApiService";
import Modal from "@/components/common/Modal";
import Button from "@/components/common/Button";
import FilterBar from "@/components/common/FilterBar";
import ClienteList from "./ClienteList";
import ClienteForm from "./ClienteForm";
import { FaPlus, FaBuilding } from "react-icons/fa";
import toastService from "@/utils/toastService";
import { normalizeDataObject } from "@/utils/normalizationUtils";

/**
 * Componente principal para la gestión de clientes con soporte multi-empresa
 * 
 * @returns {JSX.Element} - Componente de mantenedor de clientes
 */
const ClientesMantenedorMultiEmpresa = () => {
  const [clientes, setClientes] = useState([]);
  const [filteredClientes, setFilteredClientes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Contexto de empresa y APIs
  const { empresaActual, loading: empresaLoading } = useSimpleEmpresa();
  // const empresaApi = useEmpresaApi(); // Ya se usa useSimpleEmpresa arriba

  // Cargar clientes al iniciar o cambiar empresa - renderizado reactivo
  useEffect(() => {
    if (empresaActual?.id) {
      fetchClientes();
    }
  }, [empresaActual?.id]);

  // Función para cargar clientes desde la API
  const fetchClientes = async () => {
    setLoading(true);
    try {
      const clientesData = await clientesApiService.getClientes(empresaActual.id);
      setClientes(clientesData);
      setFilteredClientes(clientesData);
    } catch (error) {
      console.error("Error al cargar clientes:", error);
      toastService.error("Error al cargar los clientes");
    } finally {
      setLoading(false);
    }
  };


  // Manejar búsqueda de clientes
  const handleSearch = (value) => {
    setSearchTerm(value);
    
    if (!value.trim()) {
      setFilteredClientes(clientes);
      return;
    }

    // Usar la API de búsqueda para mejor performance
    clientesApiService.searchClientes(empresaActual.id, value)
      .then(searchResults => {
        //console.log('🔍 Resultados de búsqueda API:', searchResults?.length || 0, 'clientes');
        //console.log('🔍 Primer resultado:', searchResults?.[0]);
        setFilteredClientes(searchResults);
      })
      .catch(error => {
        console.error("Error al buscar clientes:", error);
        // Fallback a búsqueda local
        //console.log('🔍 Usando búsqueda local fallback');
        const searchLower = value.toLowerCase();
        const filtered = clientes.filter(cliente => {
          const normalizedCliente = normalizeDataObject(cliente, 'cliente');
          const nombre = (normalizedCliente.nombre || normalizedCliente.Nombre || '').toLowerCase();
          const apellido = (normalizedCliente.apellido || normalizedCliente.Apellido || '').toLowerCase();
          const email = (normalizedCliente.email || normalizedCliente.Email || '').toLowerCase();
          const rut = (normalizedCliente.rut || normalizedCliente.Rut || '').toLowerCase();
          
          return nombre.includes(searchLower) || 
                 apellido.includes(searchLower) || 
                 email.includes(searchLower) || 
                 rut.includes(searchLower);
        });
        //console.log('🔍 Resultados búsqueda local:', filtered?.length || 0, 'clientes');
        setFilteredClientes(filtered);
      });
  };

  // Manejar limpieza de filtros
  const handleResetFilters = () => {
    setSearchTerm('');
    setFilteredClientes(clientes);
    //console.log('🔄 Filtros limpiados - mostrando todos los clientes');
  };

  // Manejar creación de cliente
  const handleCreateCliente = async (clienteData) => {
    try {
      setLoading(true);
      await clientesApiService.createCliente(empresaActual.id, clienteData);
      
      toastService.success("Cliente creado exitosamente");
      setIsModalOpen(false);
      setSelectedCliente(null);
      
      // Recargar datos
      await fetchClientes();
    } catch (error) {
      console.error("Error al crear cliente:", error);
      toastService.error(error.message || "Error al crear el cliente");
    } finally {
      setLoading(false);
    }
  };

  // Manejar actualización de cliente
  const handleUpdateCliente = async (id, clienteData) => {
    try {
      setLoading(true);
      await clientesApiService.updateCliente(empresaActual.id, id, clienteData);
      
      toastService.success("Cliente actualizado exitosamente");
      setIsModalOpen(false);
      setSelectedCliente(null);
      
      // Recargar datos
      await fetchClientes();
    } catch (error) {
      console.error("Error al actualizar cliente:", error);
      toastService.error(error.message || "Error al actualizar el cliente");
    } finally {
      setLoading(false);
    }
  };

  // Manejar eliminación de cliente
  const handleDeleteCliente = async (id) => {
    if (!window.confirm("¿Estás seguro de que deseas eliminar este cliente?")) {
      return;
    }

    try {
      setLoading(true);
      await clientesApiService.deleteCliente(empresaActual.id, id);
      
      toastService.success("Cliente eliminado exitosamente");
      
      // Recargar datos
      await fetchClientes();
    } catch (error) {
      console.error("Error al eliminar cliente:", error);
      toastService.error(error.message || "Error al eliminar el cliente");
    } finally {
      setLoading(false);
    }
  };

  // Manejar apertura de modal para editar
  const handleEditCliente = (cliente) => {
    setSelectedCliente(cliente);
    setIsModalOpen(true);
  };

  // Manejar apertura de modal para crear
  const handleOpenCreateModal = () => {
    setSelectedCliente(null);
    setIsModalOpen(true);
  };

  // Manejar cierre de modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCliente(null);
  };

  // COMENTADO: Permitir renderizado reactivo sin loading screens
  /*
  // Mostrar loading si la empresa está cargando
  if (empresaLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-300">Cargando empresa...</p>
        </div>
      </div>
    );
  }

  // Mostrar error si no hay empresa seleccionada
  if (!empresaActual?.id) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <FaBuilding className="text-gray-400 text-6xl mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">No hay empresa seleccionada</h2>
          <p className="text-gray-300">Selecciona una empresa para gestionar clientes</p>
        </div>
      </div>
    );
  }
  */

  return (
    <div className="min-h-screen bg-gray-900 p-6">
        {/* Header con información de empresa */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-white">Gestión de Clientes</h1>
              <p className="text-gray-400 flex items-center mt-1">
                <FaBuilding className="mr-2" />
                {empresaActual?.nombre || 'Cargando...'} : {empresaActual?.codigo_empresa || 'Cargando...'}
              </p>
            </div>
            <Button
              onClick={handleOpenCreateModal}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={loading}
            >
              <FaPlus className="mr-2" />
              Nuevo Cliente
            </Button>
          </div>

        </div>

        {/* Barra de filtros */}
        <FilterBar
          searchValue={searchTerm}
          onSearch={handleSearch}
          onReset={handleResetFilters}
          searchPlaceholder="Buscar clientes por nombre, email o RUT..."
          className="mb-6"
        />

        {/* Lista de clientes */}
        {loading ? (
          <div className="flex justify-center py-6">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <ClienteList
            clientes={filteredClientes}
            onEdit={handleEditCliente}
            onDelete={handleDeleteCliente}
          />
        )}

        {/* Modal para crear/editar cliente */}
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={selectedCliente ? "Editar Cliente" : "Nuevo Cliente"}
          size="2xl"
        >
          <ClienteForm
            initialData={selectedCliente || {}}
            onSubmit={selectedCliente ? 
              (data) => {
                const normalizedCliente = normalizeDataObject(selectedCliente, 'cliente');
                const clienteId = normalizedCliente.id || normalizedCliente.Id;
                handleUpdateCliente(clienteId, data);
              } : 
              handleCreateCliente
            }
            onCancel={handleCloseModal}
            loading={loading}
          />
        </Modal>
    </div>
  );
};

export default ClientesMantenedorMultiEmpresa;